import React from 'react'
import PropTypes from 'prop-types'
import { Button, Checkbox } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getChoiceAnswerValue,
  getShouldRenderNextQuestion,
  resolveChoiceIdFromStoredAnswer,
} from '../../utils/instrumentUtils'

class Likert extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      selected: null,
      selectingChoice: null,
      deselectingChoice: null,
      completed: false
    }
    this.buttonRef = React.createRef()
  }

  componentDidMount() {
    const { storedAnswer, suppressAutoAdvance } = this.props

    const restoredChoiceId = resolveChoiceIdFromStoredAnswer(
      this.props.question?.choices,
      storedAnswer?.[0],
    )
    if (restoredChoiceId) {
      this.onSelectChoice(restoredChoiceId)
      // Complete immediately if not suppressing auto-advance
      if (!suppressAutoAdvance) {
        this.onCompleteLikertQuestion()
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { selected } = this.state
    if (selected !== prevState.selected) {
      this.setState({ deselectingChoice: prevState.selected })
      // Animation cleanup handled by onAnimationEnd event
    }
  }

  onSetCompletion = () => {
    const { selected } = this.state
    if (selected) {
      this.setState({ completed: true })
    } else {
      this.setState({ completed: false })
    }
  }

  createAnswerArr = () => {
    const { question, id } = this.props
    const { choices, choices_order } = question
    const { selected } = this.state
    const value = getChoiceAnswerValue(
      choices,
      selected,
      `${choices_order.indexOf(selected) + 1}`,
    )

    if (selected) {
      return [
        {
          choice_id: selected,
          label: choices[selected].label,
          question: id,
          value
        }
      ]
    }
    return []
  }

  onCompleteLikertQuestion = () => {
    const {
      question,
      id,
      completeQuestion,
      nextQ,
      webSurvey,
      submissionSurvey,
      nextQId,
      reviewFunctionalitySupported,
      isConsent = false,
      isLastQuestion = false,
      isSibling = false,
    } = this.props
    const { selected } = this.state
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = this.createAnswerArr()
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: selected ? 'ANSWERED' : 'SKIPPED',
      answer
    }
    const currentAnswer = { [id]: answer }

    const shouldRenderNextQ = getShouldRenderNextQuestion({
      isConsent,
      isLastQuestion,
      isSibling,
    })

    checkLogicAndComplete(
      webSurvey,
      id,
      submissionSurvey,
      currentAnswer,
      nextQId,
      nextQ,
      completeQuestion,
      submission,
      reviewFunctionalitySupported,
      shouldRenderNextQ
    )
  }

  onSelectChoice = choice => {
    const { setAllRequiredAnswered, id } = this.props
    this.setState({ selected: choice, selectingChoice: choice }, () => {
      setAllRequiredAnswered(false, id)
      this.onSetCompletion()
      // Autosave latest selection into Redux so sibling pages can enable Next
      // without requiring per-question footers.
      try {
        const { question, webSurvey, completeQuestion } = this.props
        const { label, type, attributes } = question
        const { required } = attributes
        const answer = this.createAnswerArr()
        const submission = {
          id,
          label,
          name: id,
          type,
          required,
          state: choice ? 'ANSWERED' : 'SKIPPED',
          answer,
        }
        if (typeof completeQuestion === 'function' && webSurvey) {
          completeQuestion(submission, webSurvey.order?.[0])
        }
      } catch (e) {
        console.warn('Likert autosave failed', { error: e })
      }
    })
    // Animation cleanup handled by onAnimationEnd event
  }

  handleAnimationEnd = (e) => {
    // Only handle our specific animation to avoid interfering with child animations
    if (e.animationName === 'selecting') {
      const { selectingChoice, deselectingChoice } = this.state
      if (selectingChoice) {
        this.setState({ selectingChoice: null })
      }
      if (deselectingChoice) {
        this.setState({ deselectingChoice: null })
      }
    }
  }

  renderChoices = (choicesOrder, likertType) => {
    const { question } = this.props
    const { choices } = question
    const { selected, selectingChoice, deselectingChoice } = this.state

    return (
      <div className='likert-choices'>
        {choicesOrder.map((choice, idx) => {
          const checked = choice === selected
          return (
            <div className='label-container' key={`${choice}`}>
              <Button
                onClick={() => this.onSelectChoice(choice)}
                onAnimationEnd={this.handleAnimationEnd}
                className={`choice${selectingChoice === choice ? ' selecting' : ''
                  }${selected === choice ? ' selected' : ''}${deselectingChoice === choice ? ' deselecting' : ''
                  }`}>
                <Checkbox
                  checked={checked}
                  transition={
                    choice === selectingChoice || choice === deselectingChoice
                  }
                />
                <span className={checked ? 'selected' : ''}>{idx + 1}</span>
              </Button>
              {likertType === 5 && (
                <p>
                  <b
                    dangerouslySetInnerHTML={{
                      __html: formatAndSanitizeHTML(choices?.[choice]?.label)
                    }}
                  />
                </p>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  render() {
    const {
      className,
      question,
      id,
      renderBackButton,
      strings,
      forwardedRef,
      isConsent,
      isLastQuestion,
      submissionLoading,
      submissionSurvey,
      webSurvey,
      isSibling = false,
    } = this.props
    const { completed } = this.state
    const { choices_order, label, choices, attributes } = question
    const { required } = attributes
    const _className = className ? ` ${className}` : ''
    const likertType = choices_order.length
    return (
      <SurveyQuestion id={id}>
        <MakeScrollable>
          <div
            ref={forwardedRef}
            className={`survey-question likert${likertType === 5 ? ' five' : ' seven'
              }${_className}`}>
            <p className='question-label'>
              <span
                dangerouslySetInnerHTML={{
                  __html: formatAndSanitizeHTML(label)
                }}
              />
            </p>
            {this.renderChoices(choices_order, likertType)}
            {likertType === 7 && (
              <div className='likert-label'>
                <span>
                  <b
                    dangerouslySetInnerHTML={{
                      __html: formatAndSanitizeHTML(
                        choices[choices_order[0]].label
                      )
                    }}
                  />
                </span>
                <span className='right'>
                  <b
                    dangerouslySetInnerHTML={{
                      __html: formatAndSanitizeHTML(
                        choices[choices_order[6]].label
                      )
                    }}
                  />
                </span>
              </div>
            )}
            {!isSibling && (
              <QuestionFooter
                completed={completed}
                id={id}
                isConsent={isConsent}
                isLastQuestion={isLastQuestion}
                loading={submissionLoading}
                onComplete={() => this.onCompleteLikertQuestion()}
                question={question}
                renderBackButton={renderBackButton}
                strings={strings}
                submissionSurvey={submissionSurvey}
                webSurvey={webSurvey}
              />
            )}
          </div>
        </MakeScrollable>
      </SurveyQuestion>
    )
  }
}

const ForwardedLikert = React.forwardRef((props, ref) => (
  <Likert {...props} forwardedRef={ref} />
))

export default ForwardedLikert

Likert.propTypes = {
  className: PropTypes.string,
  completeQuestion: PropTypes.func,
  id: PropTypes.string,
  nextQ: PropTypes.func,
  nextQId: PropTypes.func,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  submissionSurvey: PropTypes.object,
  webSurvey: PropTypes.object,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ])
}
